/**
 * CreditCardForm コンポーネント専用スタイル定義
 */

// 色の定数
const COLORS = {
  warning: '#f0ad4e',
  warningBg: '#fff3cd',
  warningBorder: '#ffeaa7',
  warningText: '#856404',
  dangerText: '#d43f06',
  white: '#fff',
  lightGray: '#f8f9fa',
  borderGray: '#dee2e6',
  textSecondary: '#495057'
};

// ブレークポイント
const BREAKPOINTS = {
  mobile: '768px'
};

/**
 * 初期費用注意書きのスタイル
 */
export const initialCostNoticeStyles = {
  container: {
    marginBottom: '25px',
    padding: '20px',
    backgroundColor: COLORS.warningBg,
    border: `1px solid ${COLORS.warningBorder}`,
    borderRadius: '8px'
  },
  
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px'
  },
  
  icon: {
    fontSize: '20px',
    color: COLORS.warning,
    marginTop: '2px'
  },
  
  title: {
    margin: '0 0 12px 0',
    color: COLORS.warningText,
    fontSize: '16px',
    fontWeight: 'bold'
  },
  
  description: {
    color: COLORS.warningText,
    fontSize: '14px',
    lineHeight: '1.6',
    marginBottom: '12px'
  },
  
  costBox: {
    backgroundColor: COLORS.white,
    border: `2px solid ${COLORS.warning}`,
    borderRadius: '6px',
    padding: '12px 16px',
    textAlign: 'center'
  },
  
  costAmount: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: COLORS.dangerText,
    marginBottom: '6px'
  },
  
  costBreakdown: {
    fontSize: '13px',
    color: COLORS.warningText
  }
};

/**
 * 会員情報表示エリアのスタイル
 */
export const memberInfoStyles = {
  container: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: COLORS.lightGray,
    borderRadius: '4px',
    border: `1px solid ${COLORS.borderGray}`
  },
  
  title: {
    margin: '0 0 10px 0',
    color: COLORS.textSecondary
  },
  
  infoItem: {
    margin: '5px 0'
  }
};

/**
 * 動的初期費用表示コンポーネント
 * 選択された店舗に応じて初期費用を動的に表示
 */
export const DynamicInitialCostNotice = ({ storeName, costInfo }) => {
  // デフォルト値を削除し、エラーハンドリングを追加
  if (!costInfo) {
    console.error('[DynamicInitialCostNotice] costInfo prop is required');
    return (
      <div style={{ padding: '20px', backgroundColor: '#f8d7da', color: '#721c24' }}>
        店舗情報が読み込まれていません。しばらくお待ちください。
      </div>
    );
  }
  
  return (
    <div style={initialCostNoticeStyles.container} className="credit-card-notice">
      <div style={initialCostNoticeStyles.content}>
        <div>
          <h4 style={initialCostNoticeStyles.title}>
            ⚠️重要：初期費用について
          </h4>
          <div style={initialCostNoticeStyles.description}>
            <span>初期費用（セキュリティキー発行費＋入会初月月額会費）の決済が同時に行われます。</span><br/>
            <span>また、カード情報入力時期にかかわらず、初月月額費用は入会初月分として充当されます。</span><br/>
            <span>当サイトは3Dセキュア (本人認証サービス)を導入しています。
            本人認証時にワンタイムパスワード等の入力を求められた場合は、画面の案内に沿って手続きを進めてください。</span>
          </div>
          {storeName && (
            <div style={{
              ...initialCostNoticeStyles.description,
              fontWeight: 'bold',
              color: '#0c5460',
              marginBottom: '8px'
            }}>
              {`選択店舗：${storeName}`}
            </div>
          )}
          <div style={initialCostNoticeStyles.costBox}>
            <div style={initialCostNoticeStyles.costAmount}>
             {`初期費用：${costInfo.cost.toLocaleString()}円（税込）`}
            </div>
            <div style={initialCostNoticeStyles.costBreakdown}>
              {`（内訳：${costInfo.description ?? ''}）`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


/**
 * 会員情報表示コンポーネント
 */
export const MemberInfoDisplay = ({ memberData, memberId }) => {
  return (
    <div style={memberInfoStyles.container} translate="no">
      <h3 style={memberInfoStyles.title}>登録された会員情報</h3>
      <p style={memberInfoStyles.infoItem}>
        <strong>お名前:</strong><span>{` ${memberData.name ?? ''}`}</span>
      </p>
      <p style={memberInfoStyles.infoItem}>
        <strong>メールアドレス:</strong><span>{` ${memberData.email ?? ''}`}</span>
      </p>
      <p style={memberInfoStyles.infoItem}>
        <strong>会員ID:</strong><span>{` ${memberId ?? ''}`}</span>
      </p>
    </div>
  );
};

/**
 * ローディング画面のスタイル
 */
export const loadingStyles = {
  container: {
    padding: '20px',
    textAlign: 'center'
  }
};

/**
 * エラー画面のスタイル
 */
export const errorStyles = {
  container: {
    padding: '20px',
    textAlign: 'center'
  }
};

/**
 * ユーティリティ関数
 */
export const creditCardFormUtils = {
  /**
   * カード番号のフォーマット
   */
  formatCardNumber: (value) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');
  },
  
  /**
   * CVVのフォーマット
   */
  formatCVV: (value) => {
    return value.replace(/\D/g, '').slice(0, 4);
  },
  
  /**
   * カード名義人のフォーマット
   */
  formatCardholderName: (value) => {
    return value.replace(/[^a-zA-Z\s]/g, '').toUpperCase().slice(0, 26);
  }
};

/**
 * モバイル用スタイル
 */
export const mobileStyles = `
  @media (max-width: ${BREAKPOINTS.mobile}) {
    .credit-card-form-container {
      padding: 10px !important;
    }
    
    .credit-card-notice {
      font-size: 13px !important;
      padding: 12px !important;
    }
    
    /* テーブルをカード形式に変換 */
    .credit-card-form-table {
      display: block !important;
      box-shadow: none !important;
    }
    
    .credit-card-form-table tbody {
      display: block !important;
    }
    
    .credit-card-form-table-row {
      display: block !important;
      margin-bottom: 15px !important;
      border: 1px solid ${COLORS.borderGray} !important;
      border-radius: 8px !important;
      padding: 12px !important;
      background-color: #fff !important;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
    }
    
    .credit-card-form-table-th,
    .credit-card-form-table-td {
      display: block !important;
      width: 100% !important;
      padding: 0 !important;
      border: none !important;
      text-align: left !important;
      background-color: transparent !important;
    }
    
    .credit-card-form-table-th {
      padding-bottom: 8px !important;
      font-size: 13px !important;
      font-weight: bold !important;
      color: #333 !important;
    }
    
    .credit-card-form-table-td {
      padding-top: 0 !important;
    }
    
    .credit-card-form-table-td input,
    .credit-card-form-table-td select {
      font-size: 16px !important;
      padding: 12px !important;
    }
    
    /* 有効期限の表示を調整 */
    .credit-card-expiry-row {
      flex-wrap: wrap !important;
    }
    
    .credit-card-expiry-row select {
      flex: 1 !important;
      min-width: 100px !important;
    }
    
    /* 送信ボタンエリア */
    .credit-card-submit-container {
      margin-top: 20px !important;
    }
    
    .credit-card-submit-button {
      width: 100% !important;
      padding: 14px 20px !important;
      font-size: 16px !important;
    }
    
    /* 会員情報表示 */
    .member-info-display {
      font-size: 13px !important;
      padding: 12px !important;
    }
    
    /* デバッグ情報 */
    .debug-info {
      font-size: 11px !important;
      padding: 8px !important;
    }
  }
`;

// スタイルシートに追加
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = mobileStyles;
  document.head.appendChild(styleElement);
}

const creditCardFormStyles = {
  initialCostNoticeStyles,
  memberInfoStyles,
  loadingStyles,
  errorStyles,
  MemberInfoDisplay,
  creditCardFormUtils,
  mobileStyles
};

export default creditCardFormStyles;